import {attachFrontendTrackingIds} from '@udemy/browse-event-tracking';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {
    fetchMoreRecommendationItems,
    flattenRecommendationSections,
    isCourseRecommendation,
    RecommendationContextSection,
    RecommendationItem,
    toDiscoveryItem,
    toDiscoveryUnits,
} from '@udemy/react-recommendations';

export type CursorMap = Map<string, string | null>;

export const isValidCourseRecommendation = (item: RecommendationItem): boolean =>
    !isCourseRecommendation(item) || !!item.course;

export const extractCursors = (sections: RecommendationContextSection[]): CursorMap =>
    new Map(
        flattenRecommendationSections(sections).flatMap(({title, itemsCursor}) =>
            title && itemsCursor ? ([[title, itemsCursor]] as [string, string][]) : [],
        ),
    );

export const toTrackedDiscoveryUnits = (
    sections: RecommendationContextSection[],
    cursors?: CursorMap,
): Partial<DiscoveryUnit>[] =>
    toDiscoveryUnits(sections).map((unit) => {
        if (unit.items) {
            attachFrontendTrackingIds(unit.items);
        }
        const hasMorePages = !!cursors?.get(unit.title ?? '');

        return {
            ...unit,
            remaining_item_count: hasMorePages ? 1 : 0,
        };
    });

export const fetchMoreTrackedItems = async (
    cursor: string,
    requestedItemCount: number,
): Promise<{
    items: Partial<DiscoveryUnit>['items'];
    nextCursor: string | null;
}> => {
    const {moreRecommendationItems} = await fetchMoreRecommendationItems({
        cursor,
        requestedItemCount,
    });

    const validItems = (moreRecommendationItems.items as RecommendationItem[])
        .filter(isValidCourseRecommendation)
        .map(toDiscoveryItem);

    attachFrontendTrackingIds(validItems);

    return {
        items: validItems,
        nextCursor: moreRecommendationItems.itemsCursor ?? null,
    };
};
